import { useState } from "react";
import { Send } from "lucide-react";
import BreadCrumb from "../../../Components/BreadCrumb";
import { FormInput, FormSection, FormSelect } from "../../../Components/Form";
import { Button } from "../../../Components/Buttons";
import { ToastMessage } from "../../../Components/Utils";

const TEMPLATES = [
  {
    value: "welcome",
    label: "Welcome Email",
    subject: "Welcome to Ukasha!",
    body: "Hi {{name}},\n\nWelcome to Ukasha! We're thrilled to have you on board.\n\nExplore our collection and enjoy exclusive offers.\n\nBest regards,\nThe Ukasha Team",
  },
  {
    value: "password_reset",
    label: "Password Reset",
    subject: "Reset Your Password",
    body: "Hi {{name}},\n\nWe received a request to reset your password. Click the link below to proceed:\n\n{{reset_link}}\n\nIf you didn't request this, ignore this email.\n\nBest regards,\nThe Ukasha Team",
  },
  {
    value: "order_confirmation",
    label: "Order Confirmation",
    subject: "Your Order #{{order_id}} is Confirmed",
    body: "Hi {{name}},\n\nThank you for your order! Your order #{{order_id}} has been confirmed and is being processed.\n\nEstimated delivery: {{delivery_date}}\n\nBest regards,\nThe Ukasha Team",
  },
  {
    value: "shipping_notification",
    label: "Shipping Notification",
    subject: "Your Order #{{order_id}} Has Shipped",
    body: "Hi {{name}},\n\nGreat news! Your order #{{order_id}} is on its way.\n\nTracking number: {{tracking_number}}\n\nBest regards,\nThe Ukasha Team",
  },
  {
    value: "newsletter",
    label: "Newsletter",
    subject: "Ukasha Newsletter — {{month}} Edition",
    body: "Hi {{name}},\n\nHere's what's new at Ukasha this month:\n\n{{content}}\n\nShop now at ukasha.com\n\nBest regards,\nThe Ukasha Team",
  },
  {
    value: "custom",
    label: "Custom",
    subject: "",
    body: "",
  },
];

const TEMPLATE_OPTIONS = TEMPLATES.map((t) => ({ value: t.value, label: t.label }));

const EMPTY = { template: "", to: "", subject: "", body: "" };

const EmailSend = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleTemplateChange = (val) => {
    const tpl = TEMPLATES.find((t) => t.value === val);
    setForm((p) => ({
      ...p,
      template: val,
      subject: tpl?.subject ?? "",
      body: tpl?.body ?? "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.to.trim()) {
      ToastMessage({ type: "error", title: "Required", message: "Recipient email is required." });
      return;
    }
    if (!form.subject.trim()) {
      ToastMessage({ type: "error", title: "Required", message: "Subject is required." });
      return;
    }
    if (!form.body.trim()) {
      ToastMessage({ type: "error", title: "Required", message: "Message body is required." });
      return;
    }

    setLoading(true);
    try {
      // TODO: wire to real send API
      await new Promise((r) => setTimeout(r, 800));
      ToastMessage({ type: "success", title: "Sent!", message: `Email sent to ${form.to}` });
      setForm(EMPTY);
    } catch {
      ToastMessage({ type: "error", title: "Failed", message: "Could not send email. Try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title="Send Email" />
      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Compose Email">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect
              label="Template"
              value={form.template}
              onChange={handleTemplateChange}
              options={TEMPLATE_OPTIONS}
              placeholder="Select a template..."
            />
            <FormInput
              isRequired
              label="Recipient Email"
              name="to"
              type="email"
              value={form.to}
              onChange={(val) => setField("to", val)}
              placeholder="recipient@example.com"
            />
          </div>

          <FormInput
            isRequired
            label="Subject"
            name="subject"
            value={form.subject}
            onChange={(val) => setField("subject", val)}
            placeholder="Email subject..."
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.body}
              onChange={(e) => setField("body", e.target.value)}
              rows={10}
              placeholder="Write your email message here..."
              className="w-full border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 transition resize-y"
            />
            {form.template && form.template !== "custom" && (
              <p className="text-xs text-gray-400 mt-1">
                Tip: Replace <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{"{{placeholders}}"}</code> with actual values before sending.
              </p>
            )}
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            onClick={() => setForm(EMPTY)}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            disabled={loading}
          >
            Clear
          </Button>
          <Button
            type="submit"
            className="!bg-orange-500 !text-white hover:!bg-orange-600 flex items-center gap-2"
            loading={loading}
            disabled={loading}
            loadingText="Sending..."
          >
            <Send size={15} />
            Send Email
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmailSend;
